import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";
import { fallbackCopyTextToClipboard } from "components/utils";

type Props = {
  open: boolean;
  plaintextValue: string;
  orgId: string;
  onClose: () => void;
};

const KeyCreatedModal: React.FC<Props> = ({
  open,
  plaintextValue,
  orgId,
  onClose,
}) => {
  const [copiedTarget, setCopiedTarget] = useState<"command" | "key" | null>(
    null,
  );
  const loginCommand = `brev login --api-key ${plaintextValue} --org-id ${orgId}`;

  const handleCopy = (value: string, target: "command" | "key") => {
    fallbackCopyTextToClipboard(value);
    setCopiedTarget(target);
    setTimeout(() => setCopiedTarget(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>API Key Created</DialogTitle>
          <DialogDescription>
            Copy your API key now. You will not be able to see it again.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-yellow-600/50 bg-yellow-900/20 p-3">
          <p className="text-sm text-yellow-400">
            This is the only time this key will be shown. Store it securely.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            CLI login command
          </label>
          <div
            onClick={() => handleCopy(loginCommand, "command")}
            className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 p-3 cursor-pointer hover:border-highlight transition-colors"
          >
            <code className="flex-1 text-sm text-highlight break-all font-mono">
              {loginCommand}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(loginCommand, "command");
              }}
              className="shrink-0 text-sm text-highlight hover:text-highlightDarker font-medium"
            >
              {copiedTarget === "command" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            API key
          </label>
          <div
            onClick={() => handleCopy(plaintextValue, "key")}
            className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 p-3 cursor-pointer hover:border-highlight transition-colors"
          >
            <code className="flex-1 text-sm text-highlight break-all font-mono">
              {plaintextValue}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(plaintextValue, "key");
              }}
              className="shrink-0 text-sm text-highlight hover:text-highlightDarker font-medium"
            >
              {copiedTarget === "key" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KeyCreatedModal;
